import { Controller } from "@hotwired/stimulus"

/**
 * SponsorshipFormController
 * 
 * Handles interactive behavior for the sponsorship form:
 * - Character counter for description field
 * - Image dimension checker with size feedback
 * - "No website" checkbox toggle for URL field
 * 
 * @example
 *   <form data-controller="sponsorship-form">
 *     <textarea data-sponsorship-form-target="description" 
 *               data-action="input->sponsorship-form#updateCharCount"></textarea>
 *     <span data-sponsorship-form-target="charCount">0</span>
 *     
 *     <img data-sponsorship-form-target="image" data-src="...">
 *     <span data-sponsorship-form-target="dimensions"></span>
 *     <div data-sponsorship-form-target="feedback" data-retina-ready="..." data-size-ok="..." data-too-small="..." data-load-error="..."></div>
 *     
 *     <input type="hidden" data-sponsorship-form-target="noWebsiteHidden">
 *     <input type="checkbox" data-sponsorship-form-target="noWebsiteCheckbox" data-action="change->sponsorship-form#toggleUrlField">
 *     <input type="url" data-sponsorship-form-target="urlField">
 *   </form>
 */
export default class extends Controller {
  static targets = [
    "description",
    "charCount",
    "image",
    "dimensions",
    "feedback",
    "noWebsiteCheckbox",
    "noWebsiteHidden",
    "urlField"
  ]

  // Display dimensions for size comparison
  static DISPLAY_WIDTH = 270
  static DISPLAY_HEIGHT = 180
  static RETINA_WIDTH = 540  // 2x for Retina
  static RETINA_HEIGHT = 360

  connect() {
    this.initCharCount()
    this.checkImageDimensions()
    this.initUrlToggle()
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Character Counter
  // ─────────────────────────────────────────────────────────────────────────────

  initCharCount() {
    if (this.hasDescriptionTarget && this.hasCharCountTarget) {
      this.updateCharCount()
    }
  }

  updateCharCount() {
    if (this.hasDescriptionTarget && this.hasCharCountTarget) {
      this.charCountTarget.textContent = this.descriptionTarget.value.length
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Image Dimension Checker
  // ─────────────────────────────────────────────────────────────────────────────

  checkImageDimensions() {
    if (!this.hasImageTarget || !this.hasDimensionsTarget) return

    const imgElement = this.imageTarget

    // Use the actual src from the img element, falling back to data-src
    const imgSrc = imgElement.src || imgElement.dataset.src
    if (!imgSrc) return

    const handleLoad = () => {
      if (imgElement.naturalWidth > 0) {
        this.displayDimensions(imgElement.naturalWidth, imgElement.naturalHeight)
      }
    }

    // Always add event listeners first
    imgElement.addEventListener('load', handleLoad, { once: true })

    imgElement.addEventListener('error', () => {
      if (this.hasFeedbackTarget) {
        this.dimensionsTarget.textContent = this.feedbackTarget.dataset.loadError
        this.dimensionsTarget.classList.add("text-danger")
      }
    }, { once: true })

    // Check if already loaded (use requestAnimationFrame to ensure DOM is ready)
    requestAnimationFrame(() => {
      if (imgElement.complete && imgElement.naturalWidth > 0) {
        handleLoad()
      }
    })
  }

  displayDimensions(width, height) {
    this.dimensionsTarget.textContent = `${width} × ${height} px`
    this.updateSizeFeedback(width, height)
  }

  updateSizeFeedback(width, height) {
    if (!this.hasFeedbackTarget) return

    const feedback = this.feedbackTarget

    if (width >= this.constructor.RETINA_WIDTH && height >= this.constructor.RETINA_HEIGHT) {
      feedback.innerHTML = `<span class="badge bg-success"><i class="fa-solid fa-circle-check me-1"></i>${feedback.dataset.retinaReady}</span>`
    } else if (width >= this.constructor.DISPLAY_WIDTH && height >= this.constructor.DISPLAY_HEIGHT) {
      feedback.innerHTML = `<span class="badge bg-info"><i class="fa-solid fa-info-circle me-1"></i>${feedback.dataset.sizeOk}</span>`
    } else {
      feedback.innerHTML = `<span class="badge bg-warning text-dark"><i class="fa-solid fa-exclamation-triangle me-1"></i>${feedback.dataset.tooSmall}</span>`
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // URL Field Toggle
  // ─────────────────────────────────────────────────────────────────────────────

  initUrlToggle() {
    if (this.hasNoWebsiteCheckboxTarget && this.hasUrlFieldTarget && this.hasNoWebsiteHiddenTarget) {
      this.toggleUrlField()
    }
  }

  toggleUrlField() {
    if (!this.hasNoWebsiteCheckboxTarget || !this.hasUrlFieldTarget || !this.hasNoWebsiteHiddenTarget) return

    const isNoWebsite = this.noWebsiteCheckboxTarget.checked

    if (isNoWebsite) {
      this.urlFieldTarget.disabled = true
      this.urlFieldTarget.value = ""
      this.urlFieldTarget.classList.add("bg-light")
      this.urlFieldTarget.removeAttribute("required")
      this.noWebsiteHiddenTarget.value = "1"
    } else {
      this.urlFieldTarget.disabled = false
      this.urlFieldTarget.classList.remove("bg-light")
      this.urlFieldTarget.setAttribute("required", "required")
      this.urlFieldTarget.focus()
      this.noWebsiteHiddenTarget.value = "0"
    }
  }
}
